import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, Database, Loader2, LockKeyhole, Rocket,
} from "lucide-react";
import { api } from "../lib/api";
import {
  CONCEPT_ARCHETYPES, CONCEPT_CATEGORIES, CONCEPT_GEOGRAPHIES,
} from "../lib/conceptOptions";

function dominantCategory(signals) {
  const counts = signals.reduce((result, signal) => {
    if (signal.category) result[signal.category] = (result[signal.category] || 0) + 1;
    return result;
  }, {});
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
}

const ScoreTrace = ({ opportunity }) => (
  <div className="border-t border-white/10 pt-3 font-mono text-xs text-bm-muted" data-testid="promotion-score-trace">
    <div className="label-mono text-bm-cyan mb-2">DETERMINISTIC EVIDENCE RUBRIC</div>
    {[
      ["Category", opportunity.scores.category],
      ["Behaviour", opportunity.scores.behaviour],
      ["Product", opportunity.scores.product],
      ["Localization", opportunity.scores.localization],
      ["Commercial", opportunity.scores.commercial],
    ].map(([label, score], index, rows) => (
      <div key={label} data-testid={`promotion-score-${label.toLowerCase()}`}>
        {index === rows.length - 1 ? "└─" : "├─"} {label.padEnd(13, " ")} {score}/100
      </div>
    ))}
  </div>
);

export const InsightOpportunityForm = ({ insight, signals }) => {
  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(() => ({
    project_id: "",
    category: CONCEPT_CATEGORIES.includes(dominantCategory(signals)) ? dominantCategory(signals) : "",
    geography_inspiration: CONCEPT_GEOGRAPHIES.includes(insight.region) ? insight.region : "",
    target_archetype: "",
    free_prompt: "",
  }));

  useEffect(() => {
    api.projects().then((data) => setProjects(data.projects || [])).catch(() => setProjects([]));
  }, []);

  if (!signals.length) {
    return (
      <section className="border border-bm-amber/50 bg-bm-amber/5 p-5" data-testid="insight-promotion-blocked">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-bm-amber shrink-0" />
          <div>
            <div className="label-mono text-bm-amber">PROMOTION BLOCKED</div>
            <p className="text-sm text-bm-muted mt-2">Exact linked Signals are required. Legacy or curated Insights without stored `signal_ids` cannot create grounded Opportunities.</p>
          </div>
        </div>
      </section>
    );
  }

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      const response = await api.promoteInsight(insight.id, form);
      setResult(response);
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(typeof detail === "string" ? detail : requestError.message || "Promotion failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="border border-bm-cyan/40 bg-bm-surface" data-testid="insight-promotion-workspace">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <div className="bg-bm-elevated p-5 border-b lg:border-b-0 lg:border-r border-white/10" data-testid="promotion-locked-evidence">
          <div className="flex items-center gap-2 label-mono text-bm-cyan">
            <LockKeyhole className="w-4 h-4" />LOCKED SOURCE EVIDENCE
          </div>
          <h2 className="font-heading text-xl font-bold mt-3">{insight.name}</h2>
          <p className="text-sm text-bm-muted mt-2 leading-relaxed">{insight.description}</p>
          <div className="flex items-center gap-2 label-mono text-bm-green mt-4" data-testid="promotion-source-count">
            <Database className="w-3.5 h-3.5" />{signals.length} EXACT SIGNAL{signals.length === 1 ? "" : "S"}
          </div>
          <div className="mt-3 space-y-2 font-mono text-xs text-bm-muted">
            {signals.slice(0, 6).map((signal, index) => (
              <div key={signal.id} className="border-l border-bm-cyan/40 pl-3" data-testid={`promotion-source-signal-${signal.id}`}>
                {index === Math.min(signals.length, 6) - 1 ? "└─" : "├─"} {signal.title}
              </div>
            ))}
            {signals.length > 6 && <div className="pl-3 text-bm-dim">└─ +{signals.length - 6} more exact Signals</div>}
          </div>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4" data-testid="insight-promotion-form">
          <div>
            <div className="label-mono text-bm-amber">PRODUCT DIRECTION INPUTS</div>
            <p className="text-xs text-bm-muted mt-1">The Insight and Signals remain fixed. These inputs shape the product direction only.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-mono text-bm-dim">House</span>
              <select value={form.project_id} onChange={(event) => setField("project_id", event.target.value)} data-testid="insight-promotion-project"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan">
                <option value="">— Any (free-form) —</option>
                {projects.map((project) => <option key={project.id} value={project.slug}>{project.name} · {project.status}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="label-mono text-bm-dim">Category</span>
              <select value={form.category} onChange={(event) => setField("category", event.target.value)} data-testid="insight-promotion-category"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan">
                {CONCEPT_CATEGORIES.map((category) => <option key={category || "any"} value={category}>{category || "— any —"}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="label-mono text-bm-dim">Geography inspiration</span>
              <select value={form.geography_inspiration} onChange={(event) => setField("geography_inspiration", event.target.value)} data-testid="insight-promotion-geography"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan">
                {CONCEPT_GEOGRAPHIES.map((geography) => <option key={geography || "any"} value={geography}>{geography || "— any —"}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="label-mono text-bm-dim">Target archetype</span>
              <select value={form.target_archetype} onChange={(event) => setField("target_archetype", event.target.value)} data-testid="insight-promotion-archetype"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan">
                {CONCEPT_ARCHETYPES.map((archetype) => <option key={archetype || "any"} value={archetype}>{archetype || "— any —"}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="label-mono text-bm-dim">Free-form product direction</span>
            <textarea value={form.free_prompt} onChange={(event) => setField("free_prompt", event.target.value)} rows={4}
              placeholder="e.g. 'single-use humidity reset for office and commute moments'"
              data-testid="insight-promotion-prompt"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan resize-y" />
          </label>

          <button type="submit" disabled={submitting} data-testid="insight-promotion-submit"
            className="w-full bg-bm-green text-black font-heading font-bold py-3 hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />GENERATING GROUNDED DRAFT...</> : <><Rocket className="w-4 h-4" />GENERATE DRAFT OPPORTUNITY</>}
          </button>

          {error && <div className="border border-bm-danger/40 p-3 text-xs font-mono text-bm-danger" data-testid="insight-promotion-error">{error}</div>}

          {result?.opportunity && (
            <div className="border border-bm-green/50 bg-bm-green/5 p-4" data-testid="insight-promotion-result">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-bm-green mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="label-mono text-bm-green">{result.already_promoted ? "EXACT DIRECTION ALREADY EXISTS" : "DRAFT OPPORTUNITY CREATED"}</div>
                  <div className="font-heading font-bold mt-1">{result.opportunity.product_nomenclature || result.opportunity.product_name}</div>
                  <Link to={`/opportunities/${result.opportunity_id}`} className="inline-flex items-center gap-1 text-xs font-mono text-bm-cyan hover:text-white mt-2 underline" data-testid="open-promoted-opportunity">
                    OPEN GROUNDED OPPORTUNITY →
                  </Link>
                </div>
              </div>
              <ScoreTrace opportunity={result.opportunity} />
            </div>
          )}
        </form>
      </div>
    </section>
  );
};